import React, { useState, useEffect } from 'react';
import SuperPhilosophyTagline from '../components/SuperPhilosophyTagline';
import { useNavigate } from 'react-router-dom';
import LaunchBanner from "../components/LaunchBanner";

const PlatformPage = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    if (window.location.hash === '#partnerships') {
      setTimeout(() => {
        document.getElementById('partnerships')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleNavigation = (path) => { window.location.href = path; };

  const FHOLogo = ({ size = 44 }) => (
    <svg width={size} height={size} viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="18" cy="18" r="16" stroke="#059669" strokeWidth="2.5" fill="none"/>
      <path d="M18 8 A10 10 0 1 1 8 18" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
      <circle cx="18" cy="18" r="4" fill="#4f46e5"/>
    </svg>
  );

  const coaches = [
    {
      name: 'EMIT / SAGE',
      subtitle: 'Biblical & Spiritual Wisdom',
      icon: '✨',
      gradient: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
      description: 'Transform wisdom into daily practice. Choose Emit (Christian) or SAGE (Spiritual) for personalized guidance through the FHO framework.',
      features: ['AI-powered Bible study', 'Daily spiritual practices', 'Consciousness progression tracking', 'Voice conversations'],
      link: '/emit-the-bible',
      color: '#4f46e5'
    },
    {
      name: 'FAMILY COACH',
      subtitle: 'Parenting & Grandparenting',
      icon: '👨‍👩‍👧‍👦',
      gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      description: 'Navigate parenting challenges with confidence. Get age-specific guidance for every stage.',
      features: ['50 parenting topics', '40 grandparent topics', 'Child age-specific advice', 'Faith-integrated guidance'],
      link: '/family-coach',
      color: '#ef4444'
    },
    {
      name: 'MENTAL PERFORMANCE',
      subtitle: 'Peak Performance Coach',
      icon: '🏆',
      gradient: 'linear-gradient(135deg, #1e3a5f 0%, #4f46e5 100%)',
      description: 'Build championship mindset. Mental performance coaching for athletes, executives, entrepreneurs, performers, and anyone who performs under pressure.',
      features: ['CHAMP (faith-based) & SHIP (secular) programs', 'Athletes, executives, entrepreneurs, performers, students', 'Pre-performance routines & resets', 'Evidence-based performance psychology'],
      link: '/mental-performance',
      color: '#4f46e5'
    },
    {
      name: 'SAFE',
      subtitle: 'Mental Health Buddy',
      icon: '🛡️',
      gradient: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
      description: 'Your judgment-free space. Work through anxiety, stress, and life challenges with compassionate support.',
      features: ['30 mental health topics', 'Military veteran support', 'Trauma-informed approach', 'Crisis resource integration'],
      link: '/safe',
      color: '#059669'
    }
  ];

  const partnerships = [
    { label: 'Health Care Sharing Ministries', icon: '✝️', route: '/hcsm', color: '#4f46e5', bg: '#eef2ff', border: '#c7d2fe' },
    { label: 'Employee Benefits Specialists', icon: '💼', route: '/ebs', color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },
    { label: 'HR Management', icon: '👥', route: '/hrm', color: '#b45309', bg: '#fef3c7', border: '#fde68a' },
    { label: 'Insurance Companies', icon: '🏥', route: '/insureco', color: '#0369a1', bg: '#e0f2fe', border: '#bae6fd' },
    { label: 'Professional Employer Orgs', icon: '🏢', route: '/peo', color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
    { label: 'Rehabilitation Facilities', icon: '🔄', route: '/rehab', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
    { label: 'Corporate Chaplaincy', icon: '🤝', route: '/chaplain', color: '#065f46', bg: '#f0fdf4', border: '#bbf7d0' },
    { label: 'Long-Term Care Facilities', icon: '🏡', route: '/ltc', color: '#0d9488', bg: '#f0fdfa', border: '#99f6e4' },
    { label: 'Social Media Influencers', icon: '📱', route: '/influencer', color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
    { label: 'Mindfulness Certification Programs', icon: '🧘', route: '/mindfulness', color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },
    { label: 'Faith-Based Nonprofits', icon: '🤲', route: '/nonprofit', color: '#1e3a5f', bg: '#eff6ff', border: '#bfdbfe' },
    { label: 'Christian Bookstores', icon: '📖', route: '/bookstore-partners', color: '#b45309', bg: '#fef3c7', border: '#fde68a' },
  ];

  const navStyle = {
    padding: isMobile ? '12px 3%' : '16px 5%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'fixed',
    top: 0, left: 0, right: 0,
    background: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(20px)',
    zIndex: 1000,
    borderBottom: '1px solid rgba(30,58,95,0.1)',
    boxShadow: '0 2px 16px rgba(30,58,95,0.08)',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#5b9bd5',
      fontFamily: "'Georgia', 'Times New Roman', serif",
      color: '#1e3a5f',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <header style={navStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', cursor: 'pointer', flexShrink: 0 }}
          onClick={() => handleNavigation('/')}>
          <FHOLogo size={isMobile ? 32 : 40} />
          <span style={{ fontSize: isMobile ? '14px' : '18px', fontWeight: '700', color: '#1e3a5f', letterSpacing: '0.02em' }}>
            <span style={{color:'#4f46e5'}}>F</span>
            <span style={{color:'#ef4444'}}>H</span>
            <span style={{color:'#059669'}}>O</span>
            {' Platform'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: isMobile ? '6px' : '10px', flexShrink: 1 }}>
          {isLoggedIn ? (
            <>
              <button onClick={() => handleNavigation('/emit-the-bible')} style={{
                padding: isMobile ? '8px 12px' : '10px 24px',
                background: '#4f46e5', border: 'none', borderRadius: '30px',
                color: 'white', fontWeight: '600', fontSize: isMobile ? '12px' : '14px',
                cursor: 'pointer', fontFamily: "'Georgia', serif"
              }}>{isMobile ? 'Dashboard' : 'Go to Dashboard'}</button>
              <button onClick={() => handleNavigation('/journey')} style={{
                padding: isMobile ? '8px 12px' : '10px 24px',
                background: 'rgba(5,150,105,0.1)', border: '1px solid rgba(5,150,105,0.4)',
                borderRadius: '30px', color: '#059669', fontWeight: '600',
                fontSize: isMobile ? '12px' : '14px', cursor: 'pointer', fontFamily: "'Georgia', serif"
              }}>{isMobile ? '✨ Journey' : '✨ My Journey'}</button>
            </>
          ) : (
            <>
              <button onClick={() => handleNavigation('/login')} style={{
                padding: isMobile ? '8px 14px' : '10px 24px',
                background: 'transparent', border: '2px solid rgba(30,58,95,0.25)',
                borderRadius: '30px', color: '#1e3a5f', fontWeight: '600',
                fontSize: isMobile ? '12px' : '14px', cursor: 'pointer', fontFamily: "'Georgia', serif"
              }}>Login</button>
              <button onClick={() => document.getElementById('partnerships').scrollIntoView({ behavior: 'smooth' })} style={{
                padding: isMobile ? '8px 14px' : '10px 24px',
                background: '#4f46e5', border: 'none', borderRadius: '30px',
                color: 'white', fontWeight: '600', fontSize: isMobile ? '12px' : '14px',
                cursor: 'pointer', fontFamily: "'Georgia', serif"
              }}>{isMobile ? 'Partner' : 'Partner With Us'}</button>
            </>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section style={{
        paddingTop: isMobile ? '100px' : '140px',
        paddingBottom: '80px',
        textAlign: 'center',
        position: 'relative',
      }}>
        <div style={{ position: 'absolute', top: '10%', left: '6%', width: '380px', height: '380px', background: 'radial-gradient(circle, rgba(79,70,229,0.25) 0%, transparent 65%)', borderRadius: '50%', filter: 'blur(50px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: '25%', right: '5%', width: '320px', height: '320px', background: 'radial-gradient(circle, rgba(239,68,68,0.2) 0%, transparent 65%)', borderRadius: '50%', filter: 'blur(50px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '5%', left: '30%', width: '420px', height: '280px', background: 'radial-gradient(circle, rgba(5,150,105,0.18) 0%, transparent 65%)', borderRadius: '50%', filter: 'blur(60px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: '0%', left: '35%', width: '340px', height: '220px', background: 'radial-gradient(circle, rgba(255,255,255,0.25) 0%, transparent 70%)', borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ marginBottom: '24px' }}>
            <span style={{
              padding: '8px 20px',
              background: 'rgba(255,255,255,0.35)',
              border: '1px solid rgba(255,255,255,0.6)',
              borderRadius: '30px', fontSize: isMobile ? '12px' : '16px', fontWeight: '600', color: '#1e3a5f'
            }}>
              {isMobile ? '✦ Faith & Spiritual · AI-Powered' : '✦ Faith & Spiritual · AI-Powered · Available 24/7 · Whole-Person Wisdom'}
            </span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
            <FHOLogo size={90} />
          </div>

          <h1 style={{
            fontSize: 'clamp(36px, 7vw, 72px)',
            fontWeight: '800',
            marginTop: '8px',
            marginBottom: '20px',
            lineHeight: '1.1',
            color: '#fff',
            textShadow: '0 2px 20px rgba(30,58,95,0.3)'
          }}>
            Whole-Person Coaching<br />Your Organization Can Offer as Its Own.
          </h1>

          <p style={{
            fontSize: 'clamp(17px, 2.5vw, 22px)',
            color: 'rgba(255,255,255,0.9)',
            maxWidth: '680px',
            margin: '0 auto 16px',
            lineHeight: '1.6'
          }}>
            FHO Platform delivers six AI coaches across four domains — spiritual wisdom, family, mental performance, and emotional wellness — that you extend to the people you serve: members, employees, clients, or community. Available 24/7, onboarded in minutes.
          </p>
          <div style={{ marginBottom: '28px' }}>
            <SuperPhilosophyTagline variant="consumer" color="#fff" />
            <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.75)', maxWidth: '640px', margin: '14px auto 0', lineHeight: '1.6' }}>
              Fhoism is a proprietary personal-development philosophy — a structured system of laws, tools, and methods for building your inner life the way an architect builds a structure: deliberately.
            </p>
            <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.75)', maxWidth: '640px', margin: '10px auto 0', lineHeight: '1.6' }}>
              Designed to support reduced anxiety, stronger family communication, clearer performance focus, and deeper spiritual grounding.
            </p>
          </div>


          {/* Persona selector */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '36px' }}>
            {[
              { label: '🏢 PEO & HR', route: '/peo' },
              { label: '💼 Employee Benefits', route: '/ebs' },
              { label: '🕊️ Chaplaincy Orgs', route: '/chaplain' },
              { label: '📖 Christian Bookstores', route: '/bookstore-partners' },
              { label: '📱 Creators & Influencers', route: '/influencer' },
            ].map(p => (
              <button key={p.route} onClick={() => handleNavigation(p.route)} style={{
                padding: '10px 20px', borderRadius: '30px', cursor: 'pointer',
                background: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.5)',
                color: '#fff', fontSize: '14px', fontWeight: '600',
                fontFamily: "'Georgia', serif",
                transition: 'all 0.2s',
              }}
              onMouseOver={e => e.target.style.background = 'rgba(255,255,255,0.28)'}
              onMouseOut={e => e.target.style.background = 'rgba(255,255,255,0.15)'}>
                {p.label}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}>
              <button onClick={() => document.getElementById('partnerships').scrollIntoView({ behavior: 'smooth' })} style={{
                padding: '16px 44px',
                background: '#4f46e5', border: 'none', borderRadius: '40px',
                color: 'white', fontSize: '17px', fontWeight: '700', cursor: 'pointer',
                boxShadow: '0 8px 30px rgba(79,70,229,0.4)',
                fontFamily: "'Georgia', serif"
              }}>Schedule a Partnership Call →</button>
              <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)', marginTop: '8px', marginBottom: 0 }}>20 minutes. No pitch. Just the platform.</p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <button onClick={() => document.getElementById('coaches').scrollIntoView({ behavior: 'smooth' })} style={{
                padding: '16px 44px',
                background: 'rgba(255,255,255,0.25)',
                border: '2px solid rgba(255,255,255,0.6)',
                borderRadius: '40px', color: 'white', fontSize: '17px', fontWeight: '600',
                cursor: 'pointer', fontFamily: "'Georgia', serif"
              }}>See How It Works</button>
              <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)', marginTop: '8px', marginBottom: 0 }}>No account required.</p>
            </div>
          </div>
        </div>
      </section>

      <LaunchBanner />

      {/* Stats Bar */}
      <section style={{
        padding: '36px 5%',
        background: 'rgba(255,255,255,0.2)',
        borderTop: '1px solid rgba(255,255,255,0.3)',
        borderBottom: '1px solid rgba(255,255,255,0.3)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '60px', flexWrap: 'wrap', maxWidth: '1000px', margin: '0 auto' }}>
          {[
            { number: '6', label: 'AI Coaches Across 4 Domains' },
            { number: '24/7', label: 'Including the 2 AM Crisis' },
            { number: '3,200+', label: 'Guided Sessions Completed' },
            { number: '12', label: 'Partnership Segments Served' }
          ].map((stat, idx) => (
            <div key={idx} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '34px', fontWeight: '800', color: '#fff' }}>{stat.number}</div>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)' }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Partnership Segments Section */}
      <section id="partnerships" style={{ padding: '100px 5%', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <h2 style={{ fontSize: 'clamp(28px, 5vw, 44px)', fontWeight: '800', color: '#fff', marginBottom: '14px', textShadow: '0 2px 12px rgba(30,58,95,0.2)' }}>
            Partnership Opportunities
          </h2>
          <p style={{ fontSize: '17px', color: 'rgba(255,255,255,0.8)', maxWidth: '600px', margin: '0 auto' }}>
            FHO Platform is available exclusively through organizational partnerships. Find your segment below.
          </p>
          <div style={{ marginTop: '20px' }}>
            <SuperPhilosophyTagline variant="partnership" color="#fff" />
            <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.75)', maxWidth: '680px', margin: '16px auto 0', lineHeight: '1.7' }}>
              FHO coaching is grounded in Fhoism — a proprietary philosophical framework with its own laws, structure, and developmental methodology, delivered progressively through the platform's coaches. This is a structured developmental system with wellness benefits.
            </p>
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '20px'
        }}>
          {partnerships.map((p, idx) => (
            <div key={idx}
              onClick={() => handleNavigation(p.route)}
              style={{
                background: 'rgba(255,255,255,0.88)',
                borderRadius: '16px',
                padding: '28px 24px',
                border: `2px solid ${p.border}`,
                backdropFilter: 'blur(16px)',
                boxShadow: '0 4px 20px rgba(30,58,95,0.10)',
                cursor: 'pointer',
                transition: 'all 0.25s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
              }}
              onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(30,58,95,0.18)'; }}
              onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(30,58,95,0.10)'; }}
            >
              <div style={{
                width: '52px', height: '52px', borderRadius: '14px',
                background: p.bg, border: `1px solid ${p.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '26px', flexShrink: 0
              }}>{p.icon}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '15px', fontWeight: '700', color: '#1e3a5f', marginBottom: '4px' }}>{p.label}</div>
                <div style={{ fontSize: '12px', color: p.color, fontWeight: '600', letterSpacing: '0.04em' }}>View Partnership →</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Coaches Section */}
      <section id="coaches" style={{ padding: '100px 5%', background: 'rgba(255,255,255,0.15)', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <h2 style={{ fontSize: 'clamp(28px, 5vw, 44px)', fontWeight: '800', color: '#fff', marginBottom: '14px', textShadow: '0 2px 12px rgba(30,58,95,0.2)' }}>
            Meet Your Transformation Team
          </h2>
          <p style={{ fontSize: '17px', color: 'rgba(255,255,255,0.8)', maxWidth: '600px', margin: '0 auto' }}>
            Six specialized coaches across four domains, each an expert, all working together for your growth
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '28px' }}>
          {coaches.map((coach, idx) => (
            <div key={idx} style={{
              background: 'rgba(255,255,255,0.82)',
              borderRadius: '20px', padding: '36px 30px',
              border: '1px solid rgba(255,255,255,0.9)',
              backdropFilter: 'blur(16px)',
              boxShadow: '0 8px 32px rgba(30,58,95,0.12)',
              transition: 'all 0.3s ease', cursor: 'pointer',
              position: 'relative', overflow: 'hidden'
            }}
              onClick={() => handleNavigation(coach.link)}
              onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-8px)'; e.currentTarget.style.boxShadow = '0 24px 50px rgba(30,58,95,0.2)'; }}
              onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(30,58,95,0.12)'; }}
            >
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: coach.gradient }} />
              <div style={{ width: '72px', height: '72px', background: coach.gradient, borderRadius: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '36px', marginBottom: '20px', boxShadow: '0 6px 20px rgba(0,0,0,0.15)' }}>{coach.icon}</div>
              <div style={{ display: 'inline-block', padding: '3px 10px', background: 'rgba(5,150,105,0.1)', border: '1px solid rgba(5,150,105,0.3)', borderRadius: '20px', fontSize: '10px', fontWeight: '700', color: '#059669', marginBottom: '12px', letterSpacing: '0.06em' }}>LIVE NOW</div>
              <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#1e3a5f', marginBottom: '6px' }}>{coach.name}</h3>
              <p style={{ fontSize: '13px', color: 'rgba(30,58,95,0.5)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '14px' }}>{coach.subtitle}</p>
              <p style={{ fontSize: '15px', color: 'rgba(30,58,95,0.75)', lineHeight: '1.6', marginBottom: '20px' }}>{coach.description}</p>
              <div style={{ marginBottom: '20px' }}>
                {coach.features.map((feature, fIdx) => (
                  <div key={fIdx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '13px', color: 'rgba(30,58,95,0.7)' }}>
                    <span style={{ color: '#059669', fontWeight: '700' }}>✓</span>{feature}
                  </div>
                ))}
              </div>
              <button style={{ width: '100%', padding: '12px', background: coach.gradient, border: 'none', borderRadius: '10px', color: 'white', fontSize: '15px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Georgia', serif" }}>
                Try {coach.name} →
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* FHO Framework Section */}
      <section style={{ padding: '100px 5%', background: 'rgba(255,255,255,0.15)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(28px, 5vw, 44px)', fontWeight: '800', color: '#fff', marginBottom: '14px', textShadow: '0 2px 12px rgba(30,58,95,0.2)' }}>
            The FHO Framework
          </h2>
          <p style={{ fontSize: '17px', color: 'rgba(255,255,255,0.8)', maxWidth: '600px', margin: '0 auto 56px' }}>
            Three pillars of transformation that guide every coaching session
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '28px' }}>
            {[
              { title: 'FREEDOM', color: '#4f46e5', border: 'rgba(79,70,229,0.5)', bg: 'rgba(79,70,229,0.08)', icon: '🔵', desc: 'Liberation from limiting beliefs. Transform your thinking and access your authentic power.', tag: 'Liberation you build, not wait for.' },
              { title: 'HAPPINESS', color: '#ef4444', border: 'rgba(239,68,68,0.5)', bg: 'rgba(239,68,68,0.08)', icon: '🔴', desc: 'Cultivate inner joy. Master your attitude and find peace regardless of circumstances.', tag: 'Joy you cultivate, not chase.' },
              { title: 'ONENESS', color: '#059669', border: 'rgba(5,150,105,0.5)', bg: 'rgba(5,150,105,0.08)', icon: '🟢', desc: 'Connect with your wholeness. Build habits that honor your complete self and others.', tag: 'Connection you demonstrate, not just feel.' }
            ].map((pillar, idx) => (
              <div key={idx} style={{
                padding: '44px 28px', background: 'rgba(255,255,255,0.75)',
                borderRadius: '20px', border: `2px solid ${pillar.border}`,
                backdropFilter: 'blur(12px)', boxShadow: '0 8px 24px rgba(30,58,95,0.1)', transition: 'all 0.3s'
              }}
                onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-8px)'; e.currentTarget.style.background = 'rgba(255,255,255,0.9)'; }}
                onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.background = 'rgba(255,255,255,0.75)'; }}
              >
                <div style={{ fontSize: '44px', marginBottom: '14px' }}>{pillar.icon}</div>
                <h3 style={{ fontSize: '28px', fontWeight: '800', color: pillar.color, marginBottom: '14px' }}>{pillar.title}</h3>
                <p style={{ fontSize: '15px', color: 'rgba(30,58,95,0.7)', lineHeight: '1.6' }}>{pillar.desc}</p>
                <p style={{ fontSize: '14px', fontStyle: 'italic', fontWeight: '600', color: pillar.color, marginTop: '12px' }}>{pillar.tag}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ padding: '100px 5%', background: 'rgba(255,255,255,0.15)', textAlign: 'center' }}>
        <h2 style={{ fontSize: 'clamp(26px, 5vw, 42px)', fontWeight: '800', color: '#fff', marginBottom: '14px', textShadow: '0 2px 12px rgba(30,58,95,0.2)' }}>
          Ready to Bring FHO to Your Organization?
        </h2>
        <p style={{ fontSize: '17px', color: 'rgba(255,255,255,0.8)', maxWidth: '600px', margin: '0 auto 36px' }}>
          Find your partnership segment and get an instant quote — no commitment required.
        </p>
        <button onClick={() => document.getElementById('partnerships').scrollIntoView({ behavior: 'smooth' })} style={{
          padding: '18px 56px', background: '#4f46e5', border: 'none',
          borderRadius: '40px', color: 'white', fontSize: '19px', fontWeight: '700',
          cursor: 'pointer', boxShadow: '0 10px 36px rgba(79,70,229,0.4)',
          fontFamily: "'Georgia', serif"
        }}>View Partnership Options →</button>
      </section>

      {/* Footer */}
      <footer style={{ padding: '36px 5%', borderTop: '1px solid rgba(255,255,255,0.25)', textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '12px' }}>
          <FHOLogo size={30} />
          <span style={{ fontWeight: '700', color: '#fff', fontSize: '15px' }}>
            <span style={{color:'#4f46e5'}}>F</span>
            <span style={{color:'#ef4444'}}>H</span>
            <span style={{color:'#059669'}}>O</span>
            {' Platform'}
          </span>
        </div>
        <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)' }}>
          © 2026 MindStreams Technology. All rights reserved. | Powered by the FHO Framework™
        </p>
        <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)', marginTop: '6px' }}>
          <a href="mailto:partnerships@freehappyone.com" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>partnerships@freehappyone.com</a>
          <span style={{ margin: '0 8px', color: 'rgba(255,255,255,0.35)' }}>·</span>
          <a href="/investors" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'underline' }}>Investors</a>
        </p>
      </footer>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default PlatformPage;
